import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Switch,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAdminPlans,
  fetchAdminFeatures,
  createAdminPlan,
  updateAdminPlan,
  replacePlanFeatures,
  fetchAdminBillingConfiguration,
  updateAdminBillingConfiguration,
  type AdminPlan,
  type PlanFeatureConfig,
  type AdminBillingConfigurationResponse,
  type EligibleTrialPlan,
  type MissingPaymentMethodBehavior,
} from "../../api/admin";

function errorMessage(error: unknown, fallback: string) {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response?: unknown }).response !== null
  ) {
    const data = (error as { response: { data?: unknown } }).response.data;
    if (
      typeof data === "object" &&
      data !== null &&
      "error" in data &&
      typeof (data as { error?: unknown }).error === "string"
    ) {
      return (data as { error: string }).error;
    }
  }
  return fallback;
}

function PlanFeatureBadges({ features }: { features?: PlanFeatureConfig[] }) {
  if (!features?.length) return <Typography sx={{ fontSize: "0.78rem", color: "#94A3B8" }}>No features configured</Typography>;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {features.map((f) => (
        <Chip
          key={f.feature_id}
          label={f.is_enabled ? (f.included_quantity != null ? `${f.feature_key} (${f.included_quantity.toLocaleString()})` : f.feature_key) : `${f.feature_key} ✕`}
          size="small"
          sx={{
            backgroundColor: f.is_enabled ? "#F0FDF4" : "#FEF2F2",
            color: f.is_enabled ? "#16A34A" : "#DC2626",
            fontSize: "0.72rem",
            fontWeight: 500,
            height: 20,
          }}
        />
      ))}
    </Box>
  );
}

function bulletsToTextareaValue(bullets: string[] | null | undefined) {
  return bullets?.join("\n") ?? "";
}

function textareaValueToBullets(value: string) {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export default function AdminPlansPage() {
  const qc = useQueryClient();

  const { data: plans = [], isLoading, isError } = useQuery({
    queryKey: ["admin", "plans"],
    queryFn: fetchAdminPlans,
  });

  const { data: allFeatures = [] } = useQuery({
    queryKey: ["admin", "features"],
    queryFn: fetchAdminFeatures,
  });

  const {
    data: billingConfiguration,
    isLoading: billingConfigurationLoading,
    isError: billingConfigurationError,
  } = useQuery({
    queryKey: ["admin", "billing-configuration"],
    queryFn: fetchAdminBillingConfiguration,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<AdminPlan | null>(null);
  const [featPlan, setFeatPlan] = useState<AdminPlan | null>(null);

  const createMutation = useMutation({
    mutationFn: createAdminPlan,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "plans"] }); setCreateOpen(false); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ planId, patch }: { planId: string; patch: Parameters<typeof updateAdminPlan>[1] }) =>
      updateAdminPlan(planId, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "plans"] }); setEditPlan(null); },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ planId, is_active }: { planId: string; is_active: boolean }) =>
      updateAdminPlan(planId, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "plans"] }),
  });

  const replaceFeaturesMutation = useMutation({
    mutationFn: ({ planId, features }: { planId: string; features: Parameters<typeof replacePlanFeatures>[1] }) =>
      replacePlanFeatures(planId, features),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "plans"] }); setFeatPlan(null); },
  });

  const billingConfigurationMutation = useMutation({
    mutationFn: updateAdminBillingConfiguration,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "billing-configuration"] });
    },
  });

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}><CircularProgress size={28} sx={{ color: "#C9734F" }} /></Box>;
  if (isError) return <Alert severity="error">Failed to load plans.</Alert>;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LayersRoundedIcon sx={{ color: "#C9734F", fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A" }}>Plans</Typography>
          <Chip label={`${plans.length} total`} size="small" sx={{ ml: 1, backgroundColor: "#F1F5F9", color: "#64748B" }} />
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: "0.82rem", boxShadow: "none" }}
        >
          New Plan
        </Button>
      </Box>

      <TrialConfigurationPanel
        data={billingConfiguration}
        loading={billingConfigurationLoading}
        loadError={billingConfigurationError}
        saving={billingConfigurationMutation.isPending}
        saveError={
          billingConfigurationMutation.error
            ? errorMessage(billingConfigurationMutation.error, "Failed to save trial configuration.")
            : null
        }
        onSave={(body) => billingConfigurationMutation.mutate(body)}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {plans.map((plan) => (
          <Paper key={plan.plan_id} elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #E2E8F0" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.92rem", color: "#0F172A" }}>{plan.display_name}</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", fontFamily: "monospace" }}>{plan.plan_key}</Typography>
                  {plan.is_public && <Chip label="public" size="small" sx={{ backgroundColor: "#EFF6FF", color: "#3B82F6", fontSize: "0.68rem", height: 18, fontWeight: 600 }} />}
                  {plan.live_payments_enabled && (
                    <Chip
                      label={`${(plan.platform_fee_percent_bps / 100).toFixed(2)}% + $${(plan.platform_fee_fixed_minor_units / 100).toFixed(2)}`}
                      size="small"
                      sx={{ backgroundColor: "#FFF7ED", color: "#C2410C", fontSize: "0.68rem", height: 18, fontWeight: 600 }}
                    />
                  )}
                </Box>
                {plan.description && <Typography sx={{ fontSize: "0.8rem", color: "#64748B", mb: 1 }}>{plan.description}</Typography>}
                {plan.marketing_bullets.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                    {plan.marketing_bullets.map((bullet) => (
                      <Chip
                        key={bullet}
                        label={bullet}
                        size="small"
                        sx={{
                          backgroundColor: "#F8FAFC",
                          color: "#475569",
                          fontSize: "0.72rem",
                          height: 20,
                        }}
                      />
                    ))}
                  </Box>
                )}
                <PlanFeatureBadges features={plan.features} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                <Tooltip title="Manage features">
                  <IconButton size="small" onClick={() => setFeatPlan(plan)} sx={{ color: "#64748B" }}>
                    <LayersRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit plan">
                  <IconButton size="small" onClick={() => setEditPlan(plan)} sx={{ color: "#64748B" }}>
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Switch
                  checked={plan.is_active}
                  size="small"
                  disabled={toggleActiveMutation.isPending}
                  onChange={(_, checked) => toggleActiveMutation.mutate({ planId: plan.plan_id, is_active: checked })}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Create Plan Dialog */}
      <CreatePlanDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        loading={createMutation.isPending}
        error={createMutation.error ? "Failed to create plan." : undefined}
      />

      {/* Edit Plan Dialog */}
      {editPlan && (
        <EditPlanDialog
          plan={editPlan}
          onClose={() => setEditPlan(null)}
          onSubmit={(patch) => updateMutation.mutate({ planId: editPlan.plan_id, patch })}
          loading={updateMutation.isPending}
          error={updateMutation.error ? "Failed to update plan." : undefined}
        />
      )}

      {/* Manage Features Dialog */}
      {featPlan && (
        <ManageFeaturesDialog
          plan={featPlan}
          allFeatures={allFeatures}
          onClose={() => setFeatPlan(null)}
          onSubmit={(features) => replaceFeaturesMutation.mutate({ planId: featPlan.plan_id, features })}
          loading={replaceFeaturesMutation.isPending}
          error={replaceFeaturesMutation.error ? "Failed to update features." : undefined}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function formatPlanPrice(plan: EligibleTrialPlan) {
  if (plan.price_monthly == null) return plan.display_name;
  return `${plan.display_name} - ${plan.currency ?? ""} ${plan.price_monthly.toLocaleString()}`.trim();
}

function formatUpdatedAt(value: string | null | undefined) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function TrialConfigurationPanel({
  data,
  loading,
  loadError,
  saving,
  saveError,
  onSave,
}: {
  data: AdminBillingConfigurationResponse | undefined;
  loading: boolean;
  loadError: boolean;
  saving: boolean;
  saveError: string | null;
  onSave: (body: {
    trial_enabled: boolean;
    default_trial_plan_id: string;
    default_trial_days: number;
    payment_method_required: boolean;
    missing_payment_method_behavior: MissingPaymentMethodBehavior;
    trial_usage_overrides: Record<string, unknown>;
  }) => void;
}) {
  const configuration = data?.configuration ?? null;
  const eligiblePlans = data?.eligible_trial_plans ?? [];
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [defaultTrialPlanId, setDefaultTrialPlanId] = useState("");
  const [defaultTrialDays, setDefaultTrialDays] = useState("30");
  const [paymentMethodRequired, setPaymentMethodRequired] = useState(false);
  const [missingPaymentMethodBehavior, setMissingPaymentMethodBehavior] =
    useState<MissingPaymentMethodBehavior>("pause");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (configuration) {
      setTrialEnabled(configuration.trial_enabled);
      setDefaultTrialPlanId(configuration.default_trial_plan_id ?? "");
      setDefaultTrialDays(String(configuration.default_trial_days ?? 30));
      setPaymentMethodRequired(configuration.payment_method_required);
      setMissingPaymentMethodBehavior(configuration.missing_payment_method_behavior);
      setValidationError(null);
      return;
    }
    if (eligiblePlans[0]) {
      setDefaultTrialPlanId(eligiblePlans[0].plan_id);
    }
  }, [configuration, eligiblePlans]);

  const selectedPlan = eligiblePlans.find((plan) => plan.plan_id === defaultTrialPlanId);
  const hasMissingConfiguration = !loading && !loadError && !configuration;
  const configPlanMissing = Boolean(configuration?.default_trial_plan_id && !selectedPlan);

  const handleSave = () => {
    const parsedDays = Number.parseInt(defaultTrialDays, 10);
    if (!Number.isInteger(parsedDays) || parsedDays < 1) {
      setValidationError("Trial days must be at least 1.");
      return;
    }
    if (!defaultTrialPlanId) {
      setValidationError("Select a default trial plan before saving.");
      return;
    }
    setValidationError(null);
    onSave({
      trial_enabled: trialEnabled,
      default_trial_plan_id: defaultTrialPlanId,
      default_trial_days: parsedDays,
      payment_method_required: paymentMethodRequired,
      missing_payment_method_behavior: missingPaymentMethodBehavior,
      trial_usage_overrides: configuration?.trial_usage_overrides ?? {},
    });
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: "12px", border: "1px solid #E2E8F0" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#0F172A", fontSize: "0.98rem" }}>
            Trial configuration
          </Typography>
          <Typography sx={{ color: "#64748B", fontSize: "0.8rem" }}>
            Controls the automatic trial assigned to new businesses.
          </Typography>
        </Box>
        {configuration && (
          <Chip
            label={`Version ${configuration.version}`}
            size="small"
            sx={{ backgroundColor: "#F8FAFC", color: "#475569", fontWeight: 600 }}
          />
        )}
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={18} sx={{ color: "#C9734F" }} />
          <Typography sx={{ color: "#64748B", fontSize: "0.82rem" }}>Loading trial configuration...</Typography>
        </Box>
      )}

      {loadError && <Alert severity="error">Failed to load trial configuration.</Alert>}

      {!loading && !loadError && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {hasMissingConfiguration && (
            <Alert severity="warning">
              No active trial configuration. New registrations will not receive automatic trial subscriptions.
            </Alert>
          )}

          {configPlanMissing && (
            <Alert severity="warning">
              The saved default trial plan is not in the eligible plan list. Select an active public Stripe-priced plan.
            </Alert>
          )}

          {validationError && <Alert severity="error">{validationError}</Alert>}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A" }}>Trials enabled</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>
                  Enable automatic trial provisioning for new registrations.
                </Typography>
              </Box>
              <Switch
                checked={trialEnabled}
                size="small"
                onChange={(_, checked) => setTrialEnabled(checked)}
                inputProps={{ "aria-label": "Trials enabled" }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" },
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A" }}>Payment method required</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>
                  Require payment details before trial access begins.
                </Typography>
              </Box>
              <Switch
                checked={paymentMethodRequired}
                size="small"
                onChange={(_, checked) => setPaymentMethodRequired(checked)}
                inputProps={{ "aria-label": "Payment method required" }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" },
                }}
              />
            </Box>

            <FormControl size="small" fullWidth>
              <InputLabel id="default-trial-plan-label">Default trial plan</InputLabel>
              <Select
                labelId="default-trial-plan-label"
                label="Default trial plan"
                value={defaultTrialPlanId}
                onChange={(event) => setDefaultTrialPlanId(event.target.value)}
              >
                {eligiblePlans.map((plan) => (
                  <MenuItem key={plan.plan_id} value={plan.plan_id}>
                    {formatPlanPrice(plan)} ({plan.plan_key})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Trial days"
              value={defaultTrialDays}
              onChange={(event) => setDefaultTrialDays(event.target.value)}
              size="small"
              type="number"
              inputProps={{ min: "1", max: "730", step: "1" }}
              fullWidth
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="missing-payment-method-behavior-label">Missing payment behavior</InputLabel>
              <Select
                labelId="missing-payment-method-behavior-label"
                label="Missing payment behavior"
                value={missingPaymentMethodBehavior}
                onChange={(event) =>
                  setMissingPaymentMethodBehavior(event.target.value as MissingPaymentMethodBehavior)
                }
              >
                <MenuItem value="pause">Pause subscription</MenuItem>
                <MenuItem value="cancel">Cancel subscription</MenuItem>
                <MenuItem value="create_invoice">Create invoice</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>Last updated</Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "#0F172A" }}>
                {formatUpdatedAt(configuration?.updated_at)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || eligiblePlans.length === 0}
              sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
            >
              {saving ? "Saving..." : "Save configuration"}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function CreatePlanDialog({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { plan_key: string; display_name: string; description?: string; marketing_bullets: string[] }) => void;
  loading: boolean;
  error?: string;
}) {
  const [planKey, setPlanKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [marketingBullets, setMarketingBullets] = useState("");

  const handleSubmit = () => {
    if (!planKey.trim() || !displayName.trim()) return;
    onSubmit({
      plan_key: planKey.trim(),
      display_name: displayName.trim(),
      description: description.trim() || undefined,
      marketing_bullets: textareaValueToBullets(marketingBullets),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A", pb: 1 }}>New Plan</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <TextField label="Plan Key" value={planKey} onChange={(e) => setPlanKey(e.target.value)} size="small" fullWidth placeholder="e.g. growth" />
        <TextField label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} size="small" fullWidth />
        <TextField
          label="Short description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={2}
          helperText="One short sentence shown above the feature bullets."
        />
        <TextField
          label="Billing page feature bullets"
          value={marketingBullets}
          onChange={(e) => setMarketingBullets(e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={4}
          helperText="One feature per line. These appear with check icons on the billing page."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#64748B" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !planKey.trim() || !displayName.trim()} sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
          {loading ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function EditPlanDialog({
  plan,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  plan: AdminPlan;
  onClose: () => void;
  onSubmit: (patch: {
    display_name?: string;
    description?: string;
    marketing_bullets?: string[];
    is_public?: boolean;
    stripe_product_id?: string | null;
    stripe_price_id?: string | null;
    platform_fee_percent_bps?: number;
    platform_fee_fixed_minor_units?: number;
    platform_fee_custom_allowed?: boolean;
    live_payments_enabled?: boolean;
    payments_required?: boolean;
    deposit_min_fixed_minor_units?: number;
    deposit_min_percent_bps?: number;
  }) => void;
  loading: boolean;
  error?: string;
}) {
  const [displayName, setDisplayName] = useState(plan.display_name);
  const [description, setDescription] = useState(plan.description ?? "");
  const [marketingBullets, setMarketingBullets] = useState(
    bulletsToTextareaValue(plan.marketing_bullets)
  );
  const [isPublic, setIsPublic] = useState(plan.is_public);
  const [stripeProductId, setStripeProductId] = useState(plan.stripe_product_id ?? "");
  const [stripePriceId, setStripePriceId] = useState(plan.stripe_price_id ?? "");
  const [feePct, setFeePct] = useState(String(plan.platform_fee_percent_bps / 100));
  const [feeFixed, setFeeFixed] = useState(String(plan.platform_fee_fixed_minor_units / 100));
  const [feeCustomAllowed, setFeeCustomAllowed] = useState(plan.platform_fee_custom_allowed);
  const [livePayments, setLivePayments] = useState(plan.live_payments_enabled);
  const [paymentsRequired, setPaymentsRequired] = useState(plan.payments_required);
  const [depositMinFixed, setDepositMinFixed] = useState(String(plan.deposit_min_fixed_minor_units / 100));
  const [depositMinPercent, setDepositMinPercent] = useState(String(plan.deposit_min_percent_bps / 100));

  const handleSubmit = () => {
    const bps = Math.round(parseFloat(feePct || "0") * 100);
    const fixed = Math.round(parseFloat(feeFixed || "0") * 100);
    const depMinFixed = Math.round(parseFloat(depositMinFixed || "0") * 100);
    const depMinPercent = Math.round(parseFloat(depositMinPercent || "0") * 100);
    onSubmit({
      display_name: displayName,
      description: description || undefined,
      marketing_bullets: textareaValueToBullets(marketingBullets),
      is_public: isPublic,
      stripe_product_id: stripeProductId.trim() || null,
      stripe_price_id: stripePriceId.trim() || null,
      platform_fee_percent_bps: bps,
      platform_fee_fixed_minor_units: fixed,
      platform_fee_custom_allowed: feeCustomAllowed,
      live_payments_enabled: livePayments,
      payments_required: paymentsRequired,
      deposit_min_fixed_minor_units: depMinFixed,
      deposit_min_percent_bps: depMinPercent,
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A", pb: 1 }}>Edit Plan — {plan.plan_key}</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <TextField label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} size="small" fullWidth />
        <TextField
          label="Short description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={2}
          helperText="One short sentence shown above the feature bullets."
        />
        <TextField
          label="Billing page feature bullets"
          value={marketingBullets}
          onChange={(e) => setMarketingBullets(e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={4}
          helperText="One feature per line. These appear with check icons on the billing page."
        />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A" }}>Public</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>Visible on the self-serve billing page</Typography>
          </Box>
          <Switch
            checked={isPublic}
            size="small"
            onChange={(_, checked) => setIsPublic(checked)}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" } }}
          />
        </Box>
        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Stripe</Typography>
        <TextField label="Stripe Product ID" value={stripeProductId} onChange={(e) => setStripeProductId(e.target.value)} size="small" fullWidth placeholder="prod_…" />
        <TextField label="Stripe Price ID" value={stripePriceId} onChange={(e) => setStripePriceId(e.target.value)} size="small" fullWidth placeholder="price_…" />
        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Platform Fees</Typography>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            label="Fee %"
            value={feePct}
            onChange={(e) => setFeePct(e.target.value)}
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0", max: "20" }}
            sx={{ flex: 1 }}
            helperText="e.g. 1.5 = 1.5%"
          />
          <TextField
            label="Fixed fee ($)"
            value={feeFixed}
            onChange={(e) => setFeeFixed(e.target.value)}
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            sx={{ flex: 1 }}
            helperText="e.g. 0.30 = 30¢"
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A" }}>Live payments enabled</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>Allow businesses on this plan to take payments</Typography>
          </Box>
          <Switch
            checked={livePayments}
            size="small"
            onChange={(_, checked) => setLivePayments(checked)}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" } }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A" }}>Allow custom fee override</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>Enterprise: let admins override fees per business</Typography>
          </Box>
          <Switch
            checked={feeCustomAllowed}
            size="small"
            onChange={(_, checked) => setFeeCustomAllowed(checked)}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" } }}
          />
        </Box>
        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Payment Requirements</Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A" }}>Payments required</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>Force this plan's businesses to always have payments enabled</Typography>
          </Box>
          <Switch
            checked={paymentsRequired}
            size="small"
            onChange={(_, checked) => setPaymentsRequired(checked)}
            inputProps={{ "aria-label": "Payments required" }}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" } }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            label="Min deposit ($)"
            value={depositMinFixed}
            onChange={(e) => setDepositMinFixed(e.target.value)}
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0", max: "10000" }}
            sx={{ flex: 1 }}
            helperText="0 = no fixed minimum"
            disabled={!paymentsRequired}
          />
          <TextField
            label="Min deposit %"
            value={depositMinPercent}
            onChange={(e) => setDepositMinPercent(e.target.value)}
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0", max: "100" }}
            sx={{ flex: 1 }}
            helperText="0 = no % minimum"
            disabled={!paymentsRequired}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#64748B" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
          {loading ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function quotaLabel(feature: import("../../api/admin").AdminFeature): string {
  return feature.feature_type === "capacity"
    ? `${feature.display_name} limit`
    : `${feature.display_name} quota`;
}

function quotaHelperText(feature: import("../../api/admin").AdminFeature): string {
  if (feature.feature_key === "web_chat_conversations") return "New webchat conversations per billing period";
  if (feature.feature_key === "voice_seconds") return "Included voice seconds per billing period";
  if (feature.storage_unit) return `${feature.storage_unit} per billing period`;
  return feature.feature_type === "capacity" ? "Maximum active items" : "Included allowance";
}

export function ManageFeaturesDialog({
  plan,
  allFeatures,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  plan: AdminPlan;
  allFeatures: import("../../api/admin").AdminFeature[];
  onClose: () => void;
  onSubmit: (features: Parameters<typeof replacePlanFeatures>[1]) => void;
  loading: boolean;
  error?: string;
}) {
  type FeatureState = { enabled: boolean; quantity: string };
  const initial: Record<string, FeatureState> = {};
  for (const f of allFeatures) {
    const existing = plan.features?.find((pf) => pf.feature_id === f.feature_id);
    initial[f.feature_id] = {
      enabled: existing?.is_enabled ?? false,
      quantity: existing?.included_quantity?.toString() ?? "",
    };
  }
  const [state, setState] = useState<Record<string, FeatureState>>(initial);

  const handleSubmit = () => {
    const features = allFeatures.map((f) => ({
      feature_id: f.feature_id,
      is_enabled: state[f.feature_id]?.enabled ?? false,
      enforcement_policy: "hard" as const,
      included_quantity: state[f.feature_id]?.quantity ? parseInt(state[f.feature_id].quantity, 10) : null,
    }));
    onSubmit(features);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A", pb: 1 }}>Features — {plan.display_name}</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {allFeatures.map((f) => (
            <Box key={f.feature_id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Switch
                checked={state[f.feature_id]?.enabled ?? false}
                size="small"
                onChange={(_, checked) => setState((s) => ({ ...s, [f.feature_id]: { ...s[f.feature_id], enabled: checked } }))}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" } }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A" }}>{f.display_name}</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", fontFamily: "monospace" }}>{f.feature_key}</Typography>
              </Box>
              {["metered", "metered_period", "capacity"].includes(f.feature_type) && (
                <TextField
                  label={quotaLabel(f)}
                  value={state[f.feature_id]?.quantity ?? ""}
                  onChange={(e) => setState((s) => ({ ...s, [f.feature_id]: { ...s[f.feature_id], quantity: e.target.value } }))}
                  size="small"
                  type="number"
                  sx={{ width: 180 }}
                  disabled={!state[f.feature_id]?.enabled}
                  placeholder="∞"
                  helperText={quotaHelperText(f)}
                />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#64748B" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
          {loading ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
